import { useState } from 'react';
import { upsertContact, upsertAssessment } from '@/services/assessmentDatabaseService';
import {
  saveAssessmentToLocal,
  getAssessmentFromLocal,
  cleanupLocalStorage,
} from '@/utils/assessmentLocalStorage';
import { ContactInfo } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';

export const useLocalAssessmentSave = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const saveToLocal = (data: any) => {
    try {
      saveAssessmentToLocal(data);
    } catch {
      console.error('[LOCAL SAVE] Unable to save assessment data locally');
    }
  };

  const saveToDatabase = async (
    contactInfo: ContactInfo,
    assessmentType: 'ia-diagnostic' | 'training-transformation',
  ): Promise<string | null> => {
    if (isSubmitting) return null;

    setIsSubmitting(true);

    try {
      const localData = getAssessmentFromLocal(assessmentType);

      if (!localData) {
        const fallbackData = getAssessmentFromLocal();
        if (!fallbackData) {
          toast({
            title: 'Erreur',
            description: `Aucune donnée d'évaluation trouvée pour ${assessmentType}. Veuillez recommencer l'évaluation.`,
            variant: 'destructive',
          });
          return null;
        }
      }

      const dataToSave = localData || getAssessmentFromLocal();
      if (!dataToSave) {
        toast({
          title: 'Erreur',
          description: "Impossible de récupérer les données de l'évaluation",
          variant: 'destructive',
        });
        return null;
      }

      const contactId = await upsertContact(contactInfo, assessmentType);
      const assessmentData = {
        ...dataToSave,
        assessmentType,
      };
      const submissionId = await upsertAssessment(contactId, assessmentData);

      if (!submissionId) {
        toast({
          title: 'Erreur de sauvegarde',
          description: "Impossible d'enregistrer vos résultats",
          variant: 'destructive',
        });
        return null;
      }

      // Une fois la sauvegarde Supabase confirmée, les réponses temporaires
      // n'ont plus de raison de rester dans le navigateur.
      cleanupLocalStorage(assessmentType);
      sessionStorage.removeItem(`assessment-draft-${assessmentType}`);

      if (!window.location.pathname.includes('audit-maturite')) {
        toast({
          title: 'Évaluation enregistrée',
          description: 'Vos résultats ont été sauvegardés avec succès !',
          duration: 3000,
        });
      }

      return submissionId;
    } catch {
      console.error(`[DATABASE SAVE] Unable to save ${assessmentType} assessment`);
      toast({
        title: 'Erreur technique',
        description: "Une erreur s'est produite lors de la sauvegarde. Merci de réessayer.",
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    saveToLocal,
    saveToDatabase,
    isSubmitting,
  };
};
